import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';

const WalletManager = ({ wallets, onWalletUpdate }) => {
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);

  // Fetch SOL price from CoinGecko
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Format balance with proper decimals
  const formatBalance = (balance) => {
    if (!balance) return '0.0000';
    return parseFloat(balance).toFixed(4);
  };

  // Format USD value
  const formatUsdValue = (balance) => {
    if (!balance || !solPrice) return '$0.00';
    const usdValue = parseFloat(balance) * solPrice;
    return `$${usdValue.toFixed(2)}`;
  };

  // Get total portfolio value
  const getTotalPortfolioValue = () => {
    if (!solPrice) return '$0.00';
    const totalSol = wallets.reduce((sum, wallet) => sum + (parseFloat(wallet.balance) || 0), 0);
    return `$${(totalSol * solPrice).toFixed(2)}`;
  };

  // Add wallet
  const addWallet = async (e) => {
    e.preventDefault();
    if (!publicKey.trim() || !privateKey.trim()) {
      alert('Please enter both public and private keys');
      return;
    }

    setLoading(true);
    try {
      const apiUrl = getApiUrl();
      await axios.post(`${apiUrl}/wallets`, {
        public_key: publicKey.trim(),
        private_key: privateKey.trim()
      });

      setPublicKey('');
      setPrivateKey('');
      alert('Wallet added successfully!');
      if (onWalletUpdate) onWalletUpdate();
    } catch (error) {
      console.error('Failed to add wallet:', error);
      alert('Failed to add wallet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Remove wallet
  const removeWallet = async (walletPublicKey) => {
    if (!window.confirm('Are you sure you want to remove this wallet?')) {
      return;
    }

    try {
      const apiUrl = getApiUrl();
      await axios.delete(`${apiUrl}/wallets/${walletPublicKey}`);
      alert('Wallet removed successfully!');
      if (onWalletUpdate) onWalletUpdate();
    } catch (error) {
      console.error('Failed to remove wallet:', error);
      alert('Failed to remove wallet. Please try again.');
    }
  };

  // Fetch SOL price on component mount
  useEffect(() => {
    fetchSolPrice();
  }, []);

  return (
    <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h2 className="text-2xl font-bold text-white mb-4 lg:mb-0">Wallet Management</h2>
        
        {/* SOL Price Indicator */}
        <div className="flex items-center space-x-4">
          <div className="backdrop-blur-sm rounded-lg px-4 py-2" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400">SOL Price</div>
            <div className="text-lg font-bold text-blue-400">
              {loadingSolPrice ? 'Loading...' : solPrice ? `$${solPrice.toFixed(2)}` : 'N/A'}
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg px-4 py-2" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400">Total Portfolio</div>
            <div className="text-lg font-bold text-green-400">
              {getTotalPortfolioValue()}
            </div>
          </div>
        </div>
      </div>

      {/* Add Wallet Form */}
      <div className="backdrop-blur-sm rounded-lg p-6 mb-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
        <h3 className="text-lg font-semibold text-white mb-4">Add New Wallet</h3>
        
        <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-3 mb-4">
          <div className="flex items-start">
            <div className="w-5 h-5 text-yellow-400 mr-2 mt-0.5">⚠️</div>
            <div>
              <p className="text-yellow-300 text-sm font-medium">Security Warning</p>
              <p className="text-yellow-200 text-xs mt-1">
                Private keys are sensitive information. Ensure you're using a secure connection and trusted environment.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={addWallet} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Public Key
            </label>
            <input
              type="text"
              value={publicKey}
              onChange={(e) => setPublicKey(e.target.value)}
              placeholder="Enter wallet public key"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Private Key
            </label>
            <div className="relative">
              <input
                type={showPrivateKey ? 'text' : 'password'}
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                placeholder="Enter wallet private key"
                className="w-full px-4 py-3 pr-12 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPrivateKey(!showPrivateKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
              >
                {showPrivateKey ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
            style={{ backgroundColor: '#353535' }}
            onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = '#22c55e'; }}
            onMouseLeave={(e) => { if (!loading) e.target.style.backgroundColor = '#353535'; }}
          >
            {loading ? 'Adding Wallet...' : 'Add Wallet'}
          </button>
        </form>
      </div>

      {/* Wallets List */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Connected Wallets ({wallets.length})</h3>
        
        {wallets.length === 0 ? (
          <div className="text-center py-12 bg-slate-900/30 border border-slate-600 rounded-lg">
            <p className="text-slate-400 text-lg">No wallets connected</p>
            <p className="text-slate-500 text-sm mt-2">Add a wallet above to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {wallets.map((wallet, index) => (
              <div key={wallet.public_key || index} className="bg-slate-900/50 border border-slate-600 rounded-lg p-4 hover:border-slate-500 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-400 mb-1">Public Key</p>
                    <p className="text-white font-mono text-xs break-all">
                      {wallet.public_key}
                    </p>
                  </div>
                  <button
                    onClick={() => removeWallet(wallet.public_key)}
                    className="ml-2 text-red-400 hover:text-red-300 p-1"
                    title="Remove wallet"
                  >
                    🗑️
                  </button>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">SOL Balance:</span>
                    <span className="text-blue-400 font-medium">
                      {formatBalance(wallet.balance)} SOL
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">USD Value:</span>
                    <span className="text-green-400 font-medium">
                      {formatUsdValue(wallet.balance)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Status:</span>
                    <span className="text-green-400 text-sm">
                      {wallet.status || 'Active'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletManager;
